import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import { GetApp, Close, LocalOffer } from '@mui/icons-material';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { pwaAdService } from '../../services/PWAAdService';

interface InstallPromptProps {
  showInHeader?: boolean;
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ showInHeader = false }) => {
  const { isInstallable, isInstalled, installApp } = usePWAInstall();
  const [showDialog, setShowDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showRewardAd, setShowRewardAd] = useState(false);

  const handleInstallClick = () => {
    if (showInHeader) {
      handleInstall();
    } else {
      setShowDialog(true);
    }
  };

  const handleInstall = async () => {
    const success = await installApp();
    setShowDialog(false);
    
    if (success) {
      setShowSuccessMessage(true);
      // Show reward ad after successful installation
      setTimeout(() => {
        setShowRewardAd(true);
      }, 2000);
      
      // Track installation with PWA ad service
      if (window.gtag) {
        window.gtag('event', 'pwa_install_completed', {
          event_category: 'PWA',
          event_label: 'Install Dialog',
          value: 1
        });
      }
    }
  };

  const handleClaimReward = () => {
    // Track reward claim
    if (window.gtag) {
      window.gtag('event', 'pwa_install_reward_claimed', {
        event_category: 'PWA',
        event_label: 'Fashion Discount',
        value: 20 // 20% discount
      });
    }
    
    // Here you would typically redirect to a fashion store or show a discount code
    window.open('https://example-fashion-store.com?discount=PWA20', '_blank');
    setShowRewardAd(false);
  };

  const handleRewardAdClose = () => {
    setShowRewardAd(false);
  };

  if (isInstalled || !isInstallable) {
    return null;
  }

  if (showInHeader) {
    return (
      <>
        <IconButton
          color="inherit"
          onClick={handleInstallClick}
          title="Install App"
          sx={{ mr: 1 }}
        >
          <GetApp />
        </IconButton>
        
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={6000}
          onClose={() => setShowSuccessMessage(false)}
        >
          <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
            App installed successfully!
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetApp />}
          onClick={handleInstallClick}
          sx={{
            borderRadius: 28,
            px: 3,
            py: 1.5,
            boxShadow: 3,
          }}
        >
          Install App
        </Button>
      </Box>

      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Install Hooliah
          <IconButton onClick={() => setShowDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" paragraph>
            Install Hooliah for a better mobile experience:
          </Typography>
          
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Quick access from your home screen
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Faster loading and better performance
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Works offline for viewing saved content
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Native app-like experience
            </Typography>
            <Typography component="li" variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              🎁 Exclusive mobile offers and discounts
            </Typography>
          </Box>

          {/* Mini promotional ad */}
          <Card sx={{ 
            background: 'linear-gradient(45deg, #f8f9fa, #e9ecef)',
            border: '1px solid #dee2e6',
            mb: 2
          }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer color="primary" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Install now and get 20% off your first fashion purchase!
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>
            Maybe Later
          </Button>
          <Button
            onClick={handleInstall}
            variant="contained"
            startIcon={<GetApp />}
          >
            Install Now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
          App installed successfully!
        </Alert>
      </Snackbar>

      {/* Reward Ad Dialog */}
      <Dialog
        open={showRewardAd}
        onClose={handleRewardAdClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          color: 'white'
        }}>
          🎉 Welcome Reward!
          <IconButton onClick={handleRewardAdClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
            Thank you for installing Hooliah!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
            As a welcome gift, enjoy 20% off your first fashion purchase from our partner stores.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            This exclusive offer is only available to app users. Don't miss out!
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleRewardAdClose}
            sx={{ color: 'rgba(255,255,255,0.8)' }}
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleClaimReward}
            variant="contained"
            sx={{ 
              background: 'white', 
              color: '#667eea',
              '&:hover': {
                background: 'rgba(255,255,255,0.9)'
              }
            }}
            startIcon={<LocalOffer />}
          >
            Claim 20% Off
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallPrompt;